import type { AiSourceRef } from "@l30/shared";

const PINNED_AI_SOURCE_TYPES: readonly string[] = [
  "note",
  "document",
  "project",
  "meeting",
  "deal",
  "ideation_map",
  "working_document",
  "sharepoint_file",
  "outlook_message",
  "subject",
];

export function sourceChipLabel(s: AiSourceRef & { title?: string; subtitle?: string }): string {
  if (s.type === "subject") {
    return s.title ? `Subject · ${s.title}` : "Subject";
  }
  if (s.type === "ideation_map") {
    return s.title ? `Knowledge project · ${s.title}` : "Knowledge project (map)";
  }
  if (s.type === "project") {
    return s.title ? `CRM project · ${s.title}` : "CRM project";
  }
  if (s.type === "working_document") {
    const base = s.title ?? "Working document";
    return s.subtitle ? `${base} · ${s.subtitle}` : base;
  }
  return s.title ?? s.type;
}

/** Best-effort navigation for Ask citation chips. */
/* eslint-disable-next-line complexity -- citation tokens span every object kind */
export function citationHref(sourceType: string, sourceId: string): string | null {
  if (sourceType === "subject") {
    if (
      sourceId.startsWith("connected-fact:") ||
      sourceId.startsWith("connected-decision:") ||
      sourceId.startsWith("connected-evidence:")
    ) {
      const originId = sourceId.split(":")[1];
      return originId ? `/subject/${originId}` : null;
    }
    if (sourceId.startsWith("subject:")) {
      const id = sourceId.split(":")[1];
      return id ? `/subject/${id}` : null;
    }
    if (sourceId.startsWith("question:") || sourceId.startsWith("neighbour:")) {
      const id = sourceId.split(":")[1];
      return id ? `/subject/${id}` : null;
    }
    if (sourceId.startsWith("assoc:note:") || sourceId.startsWith("evidence:note:")) {
      const id = sourceId.split(":")[2];
      return id ? `/notes/${id}` : null;
    }
    if (sourceId.startsWith("assoc:document:") || sourceId.startsWith("evidence:document:")) {
      const id = sourceId.split(":")[2];
      return id ? `/documents/${id}` : null;
    }
    if (sourceId.startsWith("assoc:vault_document:")) {
      const id = sourceId.split(":")[2];
      return id ? `/documents/${id}` : null;
    }
    if (sourceId.startsWith("assoc:project:") || sourceId.startsWith("evidence:project:")) {
      const id = sourceId.split(":")[2];
      return id ? `/projects/${id}` : null;
    }
    if (sourceId.startsWith("assoc:company:")) {
      const id = sourceId.split(":")[2];
      return id ? `/companies/${id}` : null;
    }
    if (sourceId.startsWith("assoc:contact:")) {
      const id = sourceId.split(":")[2];
      return id ? `/contacts/${id}` : null;
    }
    if (sourceId.startsWith("assoc:meeting:")) {
      const id = sourceId.split(":")[2];
      return id ? `/meetings/${id}` : null;
    }
    return null;
  }
  if (sourceType === "note") return `/notes/${sourceId}`;
  if (sourceType === "document") return `/documents/${sourceId}`;
  if (sourceType === "project") return `/projects/${sourceId}`;
  if (sourceType === "meeting") return `/meetings/${sourceId}`;
  if (sourceType === "working_document") return `/workspaces`;
  if (sourceType === "ideation_map") return `/ideation/${sourceId}`;
  return null;
}

export function pinnedSourcesFromUrl(): Array<AiSourceRef & { title?: string }> {
  if (typeof window === "undefined") return [];
  const params = new URLSearchParams(window.location.search);
  const type = params.get("sourceType");
  const id = params.get("sourceId");
  const title = params.get("sourceTitle") ?? undefined;
  if (type && id && PINNED_AI_SOURCE_TYPES.includes(type)) {
    return [{ type: type as AiSourceRef["type"], id, title }];
  }
  return [];
}
